import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { appReducer, uploadActions, uploadSelectors, configSelectors, configActions } from '@app-root-store';
import { Observable, Subject } from 'rxjs';
import { IFolder } from '@app-root-interfaces';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { takeUntil, debounceTime, distinctUntilKeyChanged, map, filter } from 'rxjs/operators';
import { SiModalService } from '@siemens/element-ng/modal';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { FolderSelectComponent } from '../folder-select/folder-select.component';
import { LanguageOptions } from '../edit-sidenav/language-options.config';
import { SelectItem } from '@siemens/element-ng/select';

@Component({
    selector: 'app-upload-sidenav',
    templateUrl: './upload-sidenav.component.html',
    styleUrls: ['./upload-sidenav.component.scss'],
    standalone: false
})
export class UploadSidenavComponent implements OnInit, OnDestroy {
    protected store$ = inject<Store<appReducer.AppState>>(Store);
    private fb = inject(FormBuilder);
    protected toastNotificationService = inject(SiToastNotificationService);
    private dialog = inject(SiModalService);

    protected destroy$: Subject<boolean> = new Subject<boolean>();

    public selectedFile$: Observable<any> = this.store$.pipe(select(uploadSelectors.selectUploadGetActiveFile));
    public orgUnitList$: Observable<string[]> = this.store$.pipe(
        select(configSelectors.selectOrgUnitList),
        map((orgUnitList: string[]) => orgUnitList.sort())
    );

    public orgUnitOptions$: Observable<SelectItem<string>[]> = this.orgUnitList$.pipe(
        map((list) => list.map((orgunit) => ({ type: 'option' as const, value: orgunit, label: orgunit })))
    );

    public languageSelectOptions: SelectItem<string>[] = LanguageOptions.map((opt) => ({
        type: 'option' as const,
        value: opt.value,
        label: opt.title
    }));

    protected fileChanges$: Observable<any> = this.selectedFile$.pipe(
        filter((file) => !!file),
        distinctUntilKeyChanged('tmp_id')
    );

    public uploadForm: FormGroup;

    get languageOptions() {
        return LanguageOptions;
    }

    get titlesData() {
        return this.uploadForm.get('titles') as FormArray;
    }

    get subtitlesData() {
        return this.uploadForm.get('subtitle') as FormArray;
    }

    get foldersData() {
        return this.uploadForm.get('parentFolders') as FormArray;
    }

    get driveData() {
        return this.uploadForm.get('parentDrive') as FormGroup;
    }

    constructor() {
        this.store$.dispatch(configActions.getConfigs());
    }

    ngOnInit() {
        this.createForm();
        this.subscribeToFileChanges();
        this.store$.dispatch(configActions.getConfigs());
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public resetActiveFile() {
        this.store$.dispatch(uploadActions.setActiveFile({ fileId: null }));
    }

    public addTitle() {
        this.titlesData.push(
            this.fb.group({
                text: [''],
                language: ['']
            })
        );
        this.uploadForm.updateValueAndValidity();
    }

    public removeTitle(index: number) {
        this.titlesData.removeAt(index);
    }

    public addSubtitle() {
        this.subtitlesData.push(
            this.fb.group({
                text: [''],
                language: ['']
            })
        );
        this.uploadForm.updateValueAndValidity();
    }

    public removeSubtitle(index: number) {
        this.subtitlesData.removeAt(index);
    }

    public addPreview(files: FileList) {
        if (files.length === 1) {
            const url = URL.createObjectURL(files.item(0));
            const name = files.item(0).name;
            const size = files.item(0).size;
            const type = files.item(0).type;
            this.store$.dispatch(uploadActions.updatePreview({ preview: { url, name, size, type } }));
        } else {
            this.toastNotificationService.queueToastNotification('warning', '', 'Please upload only one file.');
        }
    }

    public addFolder() {
        const ref = this.dialog.show(FolderSelectComponent, {
            animated: true,
            inputValues: {
                data: {
                    edit: false
                }
            }
        });
        ref.hidden.subscribe((result) => {
            if (result && result.payload && result.type) {
                switch (result.type) {
                    case 'folder':
                        this.addFolderToForm(result.payload);
                        break;
                    case 'drive':
                        this.addDriveToForm(result.payload);
                        break;
                    default:
                }
            }
        });
    }

    private addFolderToForm(folder: IFolder) {
        const duplicateFolders = this.foldersData.controls.filter((control) => control.get('id').value === folder.id);
        if (duplicateFolders.length === 0) {
            this.foldersData.push(
                this.fb.group({
                    id: [folder.id],
                    orgUnit: [[], { updateOn: 'blur', validators: [] }],
                    name: [folder.name],
                    filePriority: [0, [Validators.required, Validators.min(0)]]
                })
            );
        }
    }

    public removeFolder(index: number) {
        this.foldersData.removeAt(index);
    }

    private addDriveToForm(folder: IFolder) {
        this.uploadForm.addControl(
            'parentDrive',
            this.fb.group({
                id: [folder.id],
                orgUnit: [[], { updateOn: 'blur', validators: [] }],
                filePriority: [0, [Validators.required, Validators.min(0)]]
            })
        );
    }

    public removeDrive() {
        this.uploadForm.removeControl('parentDrive');
    }

    protected createForm() {
        this.uploadForm = this.fb.group({
            titles: this.fb.array([this.getEmptyMultiLang()]),
            subtitle: this.fb.array([this.getEmptyMultiLang()]),
            author: ['', Validators.required],
            version: ['', Validators.required],
            language: [[], Validators.required],
            parentFolders: this.fb.array([]),
            parentDrive: this.fb.group({})
        });
        this.selectedFile$.pipe(takeUntil(this.destroy$)).subscribe((file) => {
            if (!file) {
                this.resetActiveFile();
            }
        });
        this.subscribeToUserChanges();
    }

    protected subscribeToUserChanges() {
        this.uploadForm.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(200)).subscribe((values) => {
            this.store$.dispatch(uploadActions.updateUploadedFile({ values }));
        });
    }

    private getEmptyMultiLang() {
        return this.fb.group({
            text: ['', Validators.required],
            language: ['', Validators.required]
        });
    }

    protected subscribeToFileChanges() {
        this.fileChanges$.pipe(takeUntil(this.destroy$)).subscribe((file) => {
            console.log(file);

            // Remove ParentDriveOrgUnits formControl
            this.uploadForm.removeControl('parentDrive');
            this.foldersData.clear();
            this.titlesData.clear();
            this.subtitlesData.clear();

            // Update form with file data
            const values = {
                language: file.languages ? file.languages.map((lang) => lang.toLowerCase()) : [],
                version: file.metaInfo ? file.metaInfo.version : '',
                author: file.metaInfo ? file.metaInfo.owner : ''
            };
            this.uploadForm.patchValue(values, {
                emitEvent: false
            });

            // Create parentDrive from and fill if file.parentDrive
            if (file.parentDrive) {
                const value = file.parentDrive.orgUnit ? file.parentDrive.orgUnit : [];
                this.uploadForm.addControl(
                    'parentDrive',
                    this.fb.group({
                        id: [file.parentDrive.id],
                        orgUnit: [
                            value,
                            {
                                updateOn: 'blur',
                                validators: []
                            }
                        ],
                        filePriority: [file.parentDrive.filePriority || 0, [Validators.required, Validators.min(0)]]
                    })
                );
            }

            // Create parent folders form fields
            if (file.parentFolders) {
                file.parentFolders.map((folder) =>
                    this.foldersData.push(
                        this.fb.group({
                            id: [folder.id],
                            orgUnit: [
                                folder.orgUnit ? folder.orgUnit : [],
                                {
                                    updateOn: 'blur',
                                    validators: []
                                }
                            ],
                            name: [folder.name],
                            filePriority: [folder.filePriority || 0, [Validators.required, Validators.min(0)]]
                        })
                    )
                );
            }

            // Add titles to form
            file.titles.map((title) => {
                this.titlesData.push(
                    this.fb.group({
                        text: [title.text, Validators.required],
                        language: [title.language, Validators.required]
                    })
                );
            });
            file.subtitle.map((subtitle) => {
                this.subtitlesData.push(
                    this.fb.group({
                        text: [subtitle.text, Validators.required],
                        language: [subtitle.language, Validators.required]
                    })
                );
            });
            // Mark form as untouched
            this.uploadForm.markAsUntouched();
        });
    }
}
